import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DifmOrderForm } from '@yola/webapp-plugins';
import { selectors, verifiers } from '@yola/subscription-manager-js';
import i18next from 'i18next';
import bowser from 'yola-bowser';
import yousers from '@yola/yousersjs';
import config from 'src/js/modules/config';
import user from 'src/js/modules/user';
import dialogs from 'src/js/modules/dialogs';
import segment from 'src/js/modules/analytics/segment';
import redirectToUrl from 'src/js/utils/redirect-to-url';
import openInNewTab from 'src/js/utils/open-in-new-tab';
import { snakeCaseObjectToCamelCase } from 'src/js/utils/convert-case';
import { DEFAULT_INITIATOR } from '../../common/constants';
import { hasGoldSubscriptionWithDifmPromo } from '../verifiers/has-gold-subscription';
import useFeatureFlags from '../../feature-flags/hooks/use-feature-flags';
import utils from '../../webapp-plugins/utils';

const {
  track,
  trackAsync,
  constants: { events },
} = segment;

const eventTraits = { businessCategory: null, siteId: null, templateBuildSlug: null };

const getErrorDialogCaptions = () => ({
  title: i18next.t('Couldn’t send email'),
  description: i18next.t(
    'For an unknown reason, your email could not be sent. Please try again later.'
  ),
  dismiss: i18next.t('Return'),
});

const DifmOrderFormDialogContainer = () => {
  const dispatch = useDispatch();
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const userData = useSelector(user.selectors.getUserData);
  const calendlyDifmPromoOnboardingUrl = useSelector(
    config.selectors.getCalendlyDifmPromoOnboardingUrl
  );
  const subscriptionsList = useSelector(selectors.getSubscriptionList);

  const [featureFlags] = useFeatureFlags(['free_trial_upsell']);

  const { free_trial_upsell: isFreeTrialUpsellFeatureFlagEnabled } = featureFlags;

  const isSafari = bowser.safari;
  const errorDialogCaptions = getErrorDialogCaptions();
  const { name, surname, email: userEmail } = userData;
  const userFullName = `${name} ${surname}`;
  const isPromoFlow = hasGoldSubscriptionWithDifmPromo();
  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const hasEverHadPaidHostingSubscription = verifiers.hasPaidHostingPackageType(subscriptionsTypes);
  const isFreeTrialAvailable =
    isFreeTrialUpsellFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;

  const onBack = ({ stepId }) => {
    track(events.DIFM_ORDER_FORM_BACK_STEP_CLICKED, {
      stepId,
      ...eventTraits,
    });
  };

  const onNext = ({ stepId }) => {
    track(events.DIFM_ORDER_FORM_NEXT_STEP_CLICKED, {
      stepId,
      ...eventTraits,
    });
  };

  const onChange = ({ stepId, targetStepId }) => {
    track(events.DIFM_ORDER_FORM_GOTO_STEP_CLICKED, {
      stepId,
      targetStepId,
      ...eventTraits,
    });
  };

  const onClose = ({ stepId }) => {
    track(events.DIFM_ORDER_FORM_CLOSE_CLICKED, {
      stepId,
      ...eventTraits,
    });
    dispatch(dialogs.actions.hide());
  };

  const onDisplay = ({ stepId }) => {
    track(events.DIFM_ORDER_FORM_DISPLAYED, {
      stepId,
      ...eventTraits,
    });
  };

  const onSubmit = async ({ fieldData, traitsData, paymentUrl, paymentData }) => {
    dispatch(dialogs.actions.show(dialogs.dialogTypes.PENDING));

    await trackAsync(events.DIFM_ORDER_FORM_COMPLETED, snakeCaseObjectToCamelCase(traitsData));

    if (isPromoFlow) {
      try {
        await yousers.requests.submitDifmPromoOrder(fieldData);

        if (isSafari) {
          // Safari's popup blocker prevents programmatic link clicks in async contexts
          // So we redirect instead of opening new tab as a fallback
          redirectToUrl(calendlyDifmPromoOnboardingUrl);
        } else {
          openInNewTab(calendlyDifmPromoOnboardingUrl);
          window.location.reload();
        }
      } catch (error) {
        console.error(error);
        dispatch(dialogs.actions.hide());
        dispatch(
          dialogs.actions.show(dialogs.dialogTypes.DIFM_ERROR_DIALOG, {
            captions: errorDialogCaptions,
            onDismiss: () => {
              dispatch(dialogs.actions.hide());
            },
          })
        );
      }

      return;
    }

    const billingUrl = isFreeTrialAvailable
      ? utils.generatePaymentUrl({ ...paymentData, trial_package: '1' })
      : paymentUrl;

    redirectToUrl(billingUrl);
  };

  return (
    <DifmOrderForm
      initialValues={{
        userFullName,
        userEmail,
      }}
      initiator={DEFAULT_INITIATOR}
      term={activeHostingSubscription?.term}
      isPromoFlow={isPromoFlow}
      onBack={onBack}
      onNext={onNext}
      onChange={onChange}
      onClose={onClose}
      onDisplay={onDisplay}
      onSubmit={onSubmit}
    />
  );
};

export default DifmOrderFormDialogContainer;
